import React, { useState } from "react";
import { App, Button, Form, Modal, Input, Col, Row } from 'antd';
import { ExclamationCircleFilled } from '@ant-design/icons';

import {
  fetchPosConnection,
  fetchPosGetInitializationResponse,
  fetchPosInitialization,
  fetchPosPolling,
  fetchPosCloseDay,
  fetchCloseDay,
} from "../../../lib/data";
import { showKeyboard, hideKeyboard } from "../../../lib/keyboard";

const { TextArea } = Input;

export default function PanelAdminModal (props) {
  const { accessToken, branchOffice, isModalOpen, handleClose, printAction, changeBranchOffice } = props;
  const { modal } = App.useApp();
  const [branchForm] = Form.useForm();

  const [isLoadingConnection, setIsLoadingConnection] = useState(false);
  const [isLoadingInitializationResponse, setIsLoadingInitializationResponse] = useState(false);
  const [isLoadingInitialization, setIsLoadingInitialization] = useState(false);
  const [isLoadingPolling, setIsLoadingPolling] = useState(false);
  const [isLoadingClose, setIsLoadingClose] = useState(false);
  const [statusMessage, setStatusMessage] = useState('Elige una opción para iniciar...');

  const runConnection = async () => {
    try {
      setIsLoadingConnection(true);
      const response = await fetchPosConnection();
      setStatusMessage(`La conexión se realizó exitósamente!
      ${JSON.stringify(response.result, null, '\t')}`);
      setIsLoadingConnection(false);
    } catch (error) {
      setIsLoadingConnection(false);
      setStatusMessage(`Ocurrio un error durante la ejecución del cierre del día: ${error.message}`);
    }
  };

  const runInitialization = async () => {
    try {
      setIsLoadingInitialization(true);
      const response = await fetchPosInitialization();
      setStatusMessage(`Resultado de la inicialización: Inicialización OK (${response.result})`);
      setIsLoadingInitialization(false);
    } catch (error) {
      setIsLoadingInitialization(false);
      setStatusMessage(`Ocurrio un error durante la inicialización: ${error.message}`);
    }
  };

  const getInitializationResponse = async () => {
    try {
      setIsLoadingInitializationResponse(true);
      const response = await fetchPosGetInitializationResponse();
      setStatusMessage(`Resultado de la última inicialización:
      ${JSON.stringify(response.result, null, '\t')}`);
      setIsLoadingInitializationResponse(false);
    } catch (error) {
      setIsLoadingInitializationResponse(false);
      setStatusMessage(`Ocurrio un error durante la obtencion de la última inicialización: ${error.message}`);
    }
  };

  const runPulling = async () => {
    try {
      setIsLoadingPolling(true);
      const response = await fetchPosPolling();
      setStatusMessage(`Resultado del polling: Polling OK (${response.result})`);
      setIsLoadingPolling(false);
    } catch (error) {
      setIsLoadingPolling(false);
      setStatusMessage(`Ocurrio un error durante el polling: ${error.message}`);
    }
  };

  const runCloseDay = async () => {
    modal.confirm({
      title: 'Confirma que quieres cerrar el día',
      icon: <ExclamationCircleFilled />,
      content: 'Esta acción no se puede deshacer',
      autoFocusButton: 'cancel',
      okText: 'Si',
      cancelText: 'No',
      async onOk() {
        try {
          setIsLoadingClose(true);

          // 1. Cierre del POS Transbank (lo critico)
          const posResponse = await fetchPosCloseDay();
          const posResult = posResponse?.result;

          if (!posResult || !posResult.voucher || !posResult.voucher.length) {
            setStatusMessage(`Cierre POS sin voucher. Respuesta:\n${JSON.stringify(posResponse, null, 2)}`);
            setIsLoadingClose(false);
            return;
          }

          // 2. Imprimir el voucher tecnico del POS directamente
          printAction(posResult.voucher);

          // 3. Notificar al backend Toliv (no critico - si falla, el cierre del POS ya quedo hecho)
          let backendWarning = '';
          try {
            const response = await fetchCloseDay(
              branchOffice.company_id,
              branchOffice.id,
              accessToken,
              posResult,
            );
            if (response && response.success === false) {
              backendWarning = `\nADVERTENCIA: backend Toliv rechazo el registro: ${JSON.stringify(response)}`;
            }
          } catch (err) {
            backendWarning = `\nADVERTENCIA: no se pudo notificar al backend Toliv: ${err.message}`;
          }

          setStatusMessage(`Cierre del POS OK!${backendWarning}\n${JSON.stringify(posResult, null, '\t')}`);
          setIsLoadingClose(false);
        } catch (error) {
          setIsLoadingClose(false);
          setStatusMessage(`Ocurrio un error durante la ejecución del cierre del día: ${error.message}`);
        }
      },
      onCancel() {
        console.log('Cancel');
      },
    });
  };

  const onClickAction = action => () => {
    if(action === 'connection') {
      return runConnection();
    }
    if(action === 'initializationResponse') {
      return getInitializationResponse();
    }
    if(action === 'initialization') {
      return runInitialization();
    }
    if(action === 'polling') {
      return runPulling();
    }
    if(action === 'close') {
      return runCloseDay();
    }
  };

  const promptChangeBranch = () => {
    branchForm.setFieldsValue({ newBranchOfficeId: branchOffice?.id?.toString() ?? '' });
    modal.confirm({
      title: 'Cambiar sucursal',
      icon: <ExclamationCircleFilled />,
      okText: 'Guardar y reiniciar',
      cancelText: 'Cancelar',
      autoFocusButton: 'cancel',
      content: (
        <Form form={branchForm} layout="vertical" style={{ marginTop: 16 }}>
          <p style={{ marginBottom: 12 }}>
            Sucursal actual: <strong>{branchOffice?.name ?? '—'}</strong> (ID {branchOffice?.id ?? '—'})
          </p>
          <Form.Item
            label="Nuevo código de comercio (branch_office_id)"
            name="newBranchOfficeId"
            rules={[{ required: true, message: 'El código es requerido' }]}>
            <Input
              placeholder="Ingresa el código nuevo"
              onFocus={showKeyboard}
              onBlur={hideKeyboard} />
          </Form.Item>
          <p style={{ color: '#8a8080', fontSize: 12 }}>
            Al guardar se limpia el carrito y la app se reinicia para cargar la nueva sucursal.
          </p>
        </Form>
      ),
      async onOk() {
        try {
          const values = await branchForm.validateFields();
          const newId = String(values.newBranchOfficeId).trim();
          if (!newId || newId === String(branchOffice?.id ?? '')) {
            setStatusMessage('El código es igual al actual, no se cambio nada.');
            return;
          }
          hideKeyboard();
          setStatusMessage(`Cambiando sucursal a ID ${newId}...`);
          if (changeBranchOffice) {
            changeBranchOffice(newId);
          }
        } catch (err) {
          return Promise.reject(err);
        }
      },
      onCancel() {
        hideKeyboard();
      },
    });
  };

  return (
    <Modal
      title="Panel Administrativo"
      open={isModalOpen}
      onCancel={handleClose}
      footer={[
        <Button key="close" onClick={handleClose}>
          Salir
        </Button>,
      ]}
      centered>
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Button
            type="primary"
            onClick={onClickAction('connection')}
            loading={isLoadingConnection} block>Conectar</Button>
        </Col>
        <Col span={24}>
          <Button
            type="primary"
            onClick={onClickAction('initializationResponse')}
            loading={isLoadingInitializationResponse} block>Resultado última Inicialización</Button>
        </Col>
        <Col span={24}>
          <Button
            type="primary"
            onClick={onClickAction('initialization')}
            loading={isLoadingInitialization} block>Inicializar</Button>
        </Col>
        <Col span={24}>
          <Button
            type="primary"
            onClick={onClickAction('polling')}
            loading={isLoadingPolling} block>Polling</Button>
        </Col>
        <Col span={24}>
          <Button
            type="primary"
            onClick={onClickAction('close')}
            loading={isLoadingClose} block>Cierre</Button>
        </Col>
        <Col span={24}>
          <Button
            type="default"
            onClick={promptChangeBranch}
            block>Cambiar sucursal</Button>
        </Col>
      </Row>
      <Form layout="vertical" style={{marginTop: '20px'}} disabled>
        <Form.Item label="Results">
          <TextArea rows={4} value={statusMessage} />
        </Form.Item>
      </Form>
    </Modal>
  );
}